import { AxiosError } from 'axios';
import FastAPIInstance, { updateAxiosHeaders } from './risksystem3-public-api';
import TokenService from './token-service';
import jwt_decode from 'jwt-decode';
import { BroadcastChannel } from 'broadcast-channel';
import type { ExternalUserType } from '../utilities/userRoles';

const BROADCAST_CHANNEL_NAME = 'raptor-auth';

interface LogoutMessage {
  type: 'logout';
  reason?: 'session_expired' | 'inactivity' | 'manual_logout';
}

interface DecryptedToken {
  exp: number;
  has_multi_clients: boolean;
  has_specific_funds: boolean;
  has_specific_resources: boolean;
  ip_address: string;
  is_email_needed: boolean;
  is_qr_code_needed: boolean;
  two_factor_auth_is_valid: boolean;
  user_client_id: string;
  user_email: string;
  user_id: string;
  user_type: ExternalUserType;
}
interface FastAPIErrorResponse {
  detail: string;
  status_code?: number;
  error?: string;
}

export class FastApiFetchError extends Error {
  public responseData: FastAPIErrorResponse | null;

  constructor(
    message: string,
    responseData: FastAPIErrorResponse | null,
    name?: string,
  ) {
    super(message);
    this.responseData = responseData;
    this.name = name || 'FastApiFetchError';
  }
}

const FastAPIlogin = async (formData: FormData) => {
  let loginResponse: any;
  let decryptedToken: DecryptedToken;

  // First we need to get the token and decode it
  try {
    loginResponse = await FastAPIInstance.post(`/v1/token`, formData);

    if (!loginResponse?.data?.access_token) {
      throw new Error('No access token received from server');
    }

    decryptedToken = jwt_decode(
      loginResponse.data.access_token,
    ) as DecryptedToken;

    // Clear localStorage and update axios headers with new token, this is incase of old token still in localStorage
    localStorage.clear();
    updateAxiosHeaders(FastAPIInstance, loginResponse.data.access_token);
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401) {
      throw new FastApiFetchError(
        'Invalid credentials',
        axiosError.response?.data,
        'token',
      );
    }
    if (axiosError.response?.status === 403) {
      throw new FastApiFetchError(
        'Error fetching token or decoding',
        axiosError.response?.data,
        'token',
      );
    }
    if (axiosError.response?.status === 404) {
      throw new FastApiFetchError(
        'User not found',
        axiosError.response?.data,
        'token',
      );
    }
    throw new FastApiFetchError(
      'Error fetching token or decoding',
      null,
      'token',
    );
  }

  return { loginResponse, decryptedToken };
};

const FastAPIlogin2fa = async (accessToken: string, formData: FormData) => {
  let loginResponse: any;
  let decryptedToken: DecryptedToken;

  // First we need to get the token and decode it
  try {
    loginResponse = await FastAPIInstance.post(
      '/v1/verification_code',
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    decryptedToken = jwt_decode(
      loginResponse.data.access_token,
    ) as DecryptedToken;

    // Clear localStorage and update axios headers with new token, this is incase of old token still in localStorage
    localStorage.clear();
    updateAxiosHeaders(FastAPIInstance, loginResponse.data.access_token);
  } catch (error) {
    throw new FastApiFetchError(
      'Error fetching token or decoding',
      null,
      'token',
    );
  }

  // Next we need to fetch the user info
  return { loginResponse, decryptedToken };
};

async function fetchFastAPIUserInfo(
  loginResponse: any,
  user_id: string,
): Promise<any> {
  // Validate user_id
  if (!user_id || user_id === 'None' || user_id === 'null') {
    throw new FastApiFetchError(
      'Invalid user ID provided',
      { detail: 'User ID is required and cannot be null or None' },
      'user',
    );
  }

  try {
    const response = await FastAPIInstance.get(
      `/v1/external_user_management/user_id/${user_id}`,
      {
        headers: {
          Authorization: `Bearer ${loginResponse.data.access_token}`,
        },
      },
    );

    if (response.status === 200) {
      TokenService.setUser({ ...loginResponse.data, ...response.data });
    }

    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    throw new FastApiFetchError(
      'Failed to fetch user data',
      axiosError.response?.data || { detail: 'Failed to fetch user data' },
      'user',
    );
  }
}

const FastAPILogout = (
  reason: 'session_expired' | 'inactivity' | 'manual_logout' = 'manual_logout',
  options: { broadcast?: boolean } = {},
) => {
  const shouldBroadcast = options.broadcast !== false;

  if (shouldBroadcast) {
    try {
      const channel = new BroadcastChannel<LogoutMessage>(
        BROADCAST_CHANNEL_NAME,
      );
      channel.postMessage({ type: 'logout', reason });
      setTimeout(() => channel.close(), 0);
    } catch (error) {
      console.error('Failed to broadcast logout:', error);
    }
  }

  localStorage.clear();
  const reasonQuery = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  const loginPath = `/raptor/login${reasonQuery}`;
  const isOnSignupOrReset =
    window.location.pathname.includes('/signup') ||
    window.location.pathname.includes('/reset-password');
  const isOnLogin = window.location.pathname.includes('/login');

  // Always redirect expired/inactive sessions to ensure the login screen
  // can display the session-expired banner and reset any 2FA state.
  if (
    !isOnSignupOrReset &&
    (!isOnLogin || reason === 'session_expired' || reason === 'inactivity')
  ) {
    window.location.assign(loginPath);
  }
};

const getCurrentUser = () => {
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  return user;
};

function getAuthHeader() {
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;

  if (user && user.access_token) {
    return { Authorization: 'Bearer ' + user.access_token };
  } else {
    return {};
  }
}

const Risksystem3PublicAuthService = {
  FastAPIlogin,
  FastAPIlogin2fa,
  fetchFastAPIUserInfo,
  FastAPILogout,
  getCurrentUser,
  getAuthHeader,
};

export default Risksystem3PublicAuthService;
